import React, { useState } from "react";
import {
  Box,
  Typography,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { useSettings } from "../../context/SettingsContext";

const General = () => {
  const { theme, setTheme, accent, setAccent, language, setLanguage } = useSettings();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  const handleOpenConfirm = () => setConfirmOpen(true);
  const handleCloseConfirm = () => setConfirmOpen(false);
  const handleCloseSnack = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackOpen(false);
  };

  const handleConfirmReset = () => {
    // Defaults used across the app
    setTheme("light");      // UI theme
    setAccent("green");     // Accent color
    setLanguage("auto");    // Language auto-detect
    setConfirmOpen(false);
    setSnackOpen(true);
  };

  return (
    <Box sx={{ bgcolor: '#ffff', minWidth: 300, maxWidth: 500, borderRadius: 3, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', p: 0, m: 0 }}>
      <Typography variant="h5" sx={{ textAlign: 'left', mb: 2, mt: 0.5, color: '#516d4f', fontWeight: 600 }}>General Settings</Typography>
      <Divider sx={{ my: 1.5, borderColor: '#e1ecc8' }} />
      {/* Theme row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.2, px: 0, borderBottom: '1px solid #e1ecc8' }}>
        <Typography sx={{ color: '#516d4f', fontSize: 15 }}>Theme</Typography>
        <FormControl variant="standard" sx={{ minWidth: 120, background: 'none', boxShadow: 'none', '.MuiInputBase-root': { background: 'none', boxShadow: 'none' } }}>
          <Select
            value={theme}
            onChange={e => setTheme(e.target.value)}
            disableUnderline
            sx={{ fontSize: 15, background: 'none', boxShadow: 'none', '& .MuiSelect-select': { pr: 2 } }}
          >
            <MenuItem value="system">System</MenuItem>
            <MenuItem value="light">Light</MenuItem>
            <MenuItem value="dark">Dark</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {/* Accent color row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.2, px: 0, borderBottom: '1px solid #e1ecc8' }}>
        <Typography sx={{ color: '#516d4f', fontSize: 15 }}>Accent color</Typography>
        <FormControl variant="standard" sx={{ minWidth: 120, background: 'none', boxShadow: 'none', '.MuiInputBase-root': { background: 'none', boxShadow: 'none' } }}>
          <Select
            value={accent}
            onChange={e => setAccent(e.target.value)}
            disableUnderline
            sx={{ fontSize: 15, background: 'none', boxShadow: 'none', '& .MuiSelect-select': { pr: 2 } }}
          >
            <MenuItem value="blue">Blue</MenuItem>
            <MenuItem value="green">Green</MenuItem>
            <MenuItem value="red">Red</MenuItem>
            <MenuItem value="purple">Purple</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {/* Language row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.2, px: 0 }}>
        <Typography sx={{ color: '#516d4f', fontSize: 15 }}>Language</Typography>
        <FormControl variant="standard" sx={{ minWidth: 120, background: 'none', boxShadow: 'none', '.MuiInputBase-root': { background: 'none', boxShadow: 'none' } }}>
          <Select
            value={language}
            onChange={e => setLanguage(e.target.value)}
            disableUnderline
            sx={{ fontSize: 15, background: 'none', boxShadow: 'none', '& .MuiSelect-select': { pr: 2 } }}
          >
            <MenuItem value="auto">Auto-detect</MenuItem>
            <MenuItem value="en">English</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Divider sx={{ my: 1.5, borderColor: '#e1ecc8' }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={handleOpenConfirm}
          size="small"
          sx={{ 
            textTransform: 'none',
            borderColor: '#a0c49d',
            color: '#516d4f',
            '&:hover': {
              borderColor: '#8ab287',
              backgroundColor: '#f7fbe8',
            },
          }}
        >
          Reset to default
        </Button>
      </Box>
      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={handleCloseConfirm} fullWidth maxWidth="xs">
        <DialogTitle sx={{ color: '#516d4f' }}>Reset settings?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#516d4f' }}>
            This will restore Theme, Accent color, and Language to their default values.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseConfirm}
            sx={{
              borderColor: '#a0c49d',
              color: '#516d4f',
              '&:hover': {
                borderColor: '#8ab287',
                backgroundColor: '#f7fbe8',
              },
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleConfirmReset}
            sx={{
              backgroundColor: '#d32f2f',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#b71c1c',
              },
            }}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar open={snackOpen} autoHideDuration={2500} onClose={handleCloseSnack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnack} severity="success" sx={{ width: '100%' }} variant="filled">
          Settings reset to default.
        </Alert>
      </Snackbar>
      {/* Spoken language, 'For best results...' text, and Voice section removed as requested */}
    </Box>
  );
};

export default General;
